import hadrianModal from '@/shared/viewParts/hadrianModal/hadrianModal.vue';
import inputGroup from '@/shared/viewParts/inputGroup/inputGroup.vue';
import tagBox from '@/shared/viewParts/tagBox/tagBox.vue';
import singleDatePicker from '@/shared/viewParts/singleDatePicker/singleDatePicker.vue';
import { DisabledDateObject } from '@/shared/viewParts/singleDatePicker/singleDatePicker';
import hadrianSelect from '@/shared/viewParts/hadrianSelect/hadrianSelect.vue';
import iconChevronRight from '@/shared/icons/chevronRight/chevronRight.vue';
import exclamation from '@/shared/icons/exclamation/exclamation.vue';
import partitionsProject from '@/shared/icons/partitionsProject/partitionsProject.vue';
import EmailHelper from '@/helpers/emailHelper';
import Toast from '@/helpers/toast';
import ModalEvents from '@/enums/modalEvents';
import EventBus from '@/eventBus';
import ProjectTasksService from '@/endpoints/projectTasksService';
import ProjectService from '@/endpoints/projectService';
import { ProjectTask, ProjectTaskCreateParams, ProjectTaskUpdateParams } from '@/interfaces/projectTask';
import moment from 'moment-timezone';
import { vxManager } from '@/store/store';
import { defineComponent } from 'vue';

export interface ProjectTaskModalShowData {
  taskId?: string;
  projectId?: string;
  projectName?: string;
  task?: ProjectTask;
  listItem?: ProjectTask;
  isRestore?: boolean;
  isDetails?: boolean;
}

export interface ProjectTaskModalRef {
  show(data: ProjectTaskModalShowData): void;
  hide(): void;
  setCallbacks(callbacks: { onSaved?: () => void; onArchived?: (taskId: string) => void }): void;
}

const CC_LIMIT = 20;
const DEFAULT_TIME = '12:00 AM';

function generateTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const min = String(m).padStart(2, '0');
      options.push(`${hour12}:${min} ${ampm}`);
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

function parse12hTo24h(timeStr: string): { h: number; m: number } {
  const [timePart, period] = timeStr.split(' ');
  const [hStr, mStr] = timePart.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return { h, m };
}

export default defineComponent({
  name: 'projectTaskModal',
  components: {
    hadrianModal,
    inputGroup,
    tagBox,
    singleDatePicker,
    hadrianSelect,
    iconChevronRight,
    exclamation,
    partitionsProject,
  },
  data() {
    return {
      CC_LIMIT,
      EmailHelper,
      TIME_OPTIONS,
      taskId: null as string | null,
      projectId: null as string | null,
      projectName: null as string | null,
      ccTagBoxKey: 0,
      notificationsExpanded: true,
      orgUsers: [] as { email: string; name: string }[],
      cerRepEmail: null as string | null,
      cerRepName: null as string | null,
      form: {
        title: '',
        description: '',
        dueDate: null as Date | null,
        dueTime: DEFAULT_TIME,
        ccEmails: [] as string[],
      },
      isRestore: false,
      isDetails: false,
      titleError: false,
      isSaving: false,
      onSaved: null as (() => void) | null,
      onArchived: null as ((taskId: string) => void) | null,
    };
  },
  computed: {
    isFormValid(): boolean {
      if (!this.form.title.trim()) return false;
      if (this.form.ccEmails.length > CC_LIMIT) return false;
      return true;
    },
    ccSuggestionList(): { label: string; value: string }[] {
      const currentUserEmail = vxManager.userModule.getUserInfo?.username;
      const suggestions = this.orgUsers
        .filter(u => u.email !== currentUserEmail)
        .map(u => ({
          label: u.name ? `${u.name} <${u.email}>` : u.email,
          value: u.email,
        }));
      if (this.cerRepEmail && !this.orgUsers.some(u => u.email === this.cerRepEmail)) {
        suggestions.push({
          label: this.cerRepName ? `${this.cerRepName} <${this.cerRepEmail}>` : this.cerRepEmail,
          value: this.cerRepEmail,
        });
      }
      return suggestions;
    },

    disabledDates(): DisabledDateObject {
      const tz = vxManager.userSettingsModule.userTimezoneId;
      const nowInTz = moment.tz(new Date(), tz);
      const nowH = nowInTz.hours();
      const nowM = nowInTz.minutes();
      // If no time slots remain for today, disable today as well (cutoff = today).
      // Otherwise only disable up to yesterday.
      const noSlotsToday = !TIME_OPTIONS.some(t => {
        const { h, m } = parse12hTo24h(t);
        return h > nowH || (h === nowH && m >= nowM);
      });
      const cutoff = noSlotsToday ? nowInTz : nowInTz.clone().subtract(1, 'day');
      return {
        to: new Date(cutoff.year(), cutoff.month(), cutoff.date(), 23, 59, 59, 999),
      };
    },
    filteredTimeOptions(): string[] {
      if (!this.form.dueDate) return TIME_OPTIONS;
      const tz = vxManager.userSettingsModule.userTimezoneId;
      const nowInTz = moment.tz(new Date(), tz);
      const sel = this.form.dueDate;
      const isToday =
        sel.getUTCFullYear() === nowInTz.year() &&
        sel.getUTCMonth() === nowInTz.month() &&
        sel.getUTCDate() === nowInTz.date();
      if (!isToday) return TIME_OPTIONS;
      const nowH = nowInTz.hours();
      const nowM = nowInTz.minutes();
      return TIME_OPTIONS.filter(t => {
        const { h, m } = parse12hTo24h(t);
        return h > nowH || (h === nowH && m >= nowM);
      });
    },
  },
  watch: {
    'form.dueDate'(newVal: Date | null) {
      if (!newVal) {
        this.form.ccEmails = [];
        this.ccTagBoxKey += 1;
        return;
      }
      if (!this.filteredTimeOptions.includes(this.form.dueTime)) {
        this.form.dueTime = this.filteredTimeOptions[0] ?? DEFAULT_TIME;
      }
    },
  },
  created() {
    EventBus.$on(ModalEvents.ProjectTask, this.show);
  },
  beforeUnmount() {
    EventBus.$off(ModalEvents.ProjectTask, this.show);
  },
  methods: {
    show(data: ProjectTaskModalShowData) {
      this.taskId = data.taskId ?? null;
      this.projectId = data.projectId ?? null;
      this.projectName = data.projectName ?? data.listItem?.projectName ?? data.task?.projectName ?? null;
      this.isRestore = data.isRestore ?? false;
      this.isDetails = data.isDetails ?? false;
      this.titleError = false;
      this.ccTagBoxKey += 1;
      this.notificationsExpanded = !!this.taskId || this.isDetails || this.isRestore;

      this.orgUsers = [];
      this.cerRepEmail = null;
      this.cerRepName = null;

      if (this.projectId) {
        ProjectService.getProjectStripped(this.projectId).then(p => {
          if (p.distributor?.id) {
            ProjectTasksService.getOrganizationUsers(p.distributor.id).then(list => {
              this.orgUsers = list;
            });
          }
        });
      }

      if (data.listItem) {
        this.populateFromData(data.listItem);
      } else if (data.task) {
        this.populateFromData(data.task);
      } else {
        this.resetForm();
      }

      if (this.isRestore && this.form.dueDate && this.form.dueDate < new Date()) {
        this.form.dueDate = null;
        this.form.dueTime = DEFAULT_TIME;
      }

      (this.$refs.modal as { show(): void }).show();
    },
    hide() {
      (this.$refs.modal as { hide(): void }).hide();
    },
    setCallbacks(callbacks: { onSaved?: () => void; onArchived?: (taskId: string) => void }) {
      this.onSaved = callbacks.onSaved ?? null;
      this.onArchived = callbacks.onArchived ?? null;
    },
    applyDueDateFromApi(dueDate: string | null | undefined) {
      if (!dueDate) {
        this.form.dueDate = null;
        this.form.dueTime = DEFAULT_TIME;
        return;
      }
      const tz = vxManager.userSettingsModule.userTimezoneId;
      const m = moment.tz(dueDate, tz);
      // Use the timezone-aware date so the calendar shows the correct local day
      // (e.g. UTC April 2 08:00 = April 1 23:00 in UTC-9 → calendar shows April 1).
      this.form.dueDate = new Date(Date.UTC(m.year(), m.month(), m.date(), 12, 0, 0));
      // Convert the UTC time back to the org's timezone for the time selector.
      const h = m.hours();
      const min = Math.floor(m.minutes() / 15) * 15;
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      this.form.dueTime = `${hour12}:${String(min).padStart(2, '0')} ${ampm}`;
    },
    populateFromData(task: ProjectTask) {
      this.form.title = task.title;
      this.form.description = task.description ?? '';
      this.applyDueDateFromApi(task.dueDate);
      this.form.ccEmails = task.ccRecipients.map(r => r.email);
      this.projectName = task.projectName;
    },
    resetForm() {
      this.form.title = '';
      this.form.description = '';
      this.form.dueDate = null;
      this.form.dueTime = DEFAULT_TIME;
      this.form.ccEmails = [];
    },
    buildDueDateParam(): string | null {
      if (!this.form.dueDate) return null;
      const tz = vxManager.userSettingsModule.userTimezoneId;
      // Extract the calendar date from the noon-UTC anchor in form.dueDate,
      // then apply the selected time in the user's configured timezone.
      const dateStr = this.form.dueDate.toISOString().substring(0, 10);
      const { h, m } = parse12hTo24h(this.form.dueTime);
      return moment.tz(dateStr, tz).hours(h).minutes(m).seconds(0).milliseconds(0).format();
    },
    clearDate() {
      this.form.dueDate = null;
    },
    onDateInput(date: Date | null) {
      if (!date) {
        this.form.dueDate = null;
        return;
      }
      this.form.dueDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0));
    },
    enterRestoreMode() {
      this.isDetails = false;
      this.isRestore = true;
      if (this.form.dueDate && this.form.dueDate < new Date()) {
        this.form.dueDate = null;
        this.form.dueTime = DEFAULT_TIME;
      }
    },
    positionCalendar() {
      setTimeout(() => {
        const anchor = (this.$refs.datePicker as any)?.$el as HTMLElement | undefined;
        if (!anchor) return;
        const cal = anchor.querySelector('.vdp-datepicker__calendar') as HTMLElement | null;
        if (!cal) return;
        const rect = anchor.getBoundingClientRect();
        cal.style.left = `${rect.left}px`;
        cal.style.top = `${rect.top - cal.offsetHeight - 4}px`;
      }, 0);
    },
    toggleNotifications() {
      this.notificationsExpanded = !this.notificationsExpanded;
    },
    async onSave() {
      if (!this.form.title.trim()) {
        this.titleError = true;
        return;
      }
      this.isSaving = true;
      try {
        let task: ProjectTask;
        if (this.taskId) {
          const params: ProjectTaskUpdateParams = {
            id: this.taskId,
            title: this.form.title.trim(),
            description: this.form.description || null,
            dueDate: this.buildDueDateParam(),
            ccRecipients: this.form.ccEmails,
          };
          task = await ProjectTasksService.updateProjectTask(params);
          const toastKey = this.isRestore ? 'restoreSuccess' : 'updateSuccess';
          Toast.success({ body: this.$t(`projectTasks.toast.${toastKey}`).toString() });
        } else {
          const params: ProjectTaskCreateParams = {
            projectId: this.projectId!,
            title: this.form.title.trim(),
            description: this.form.description || null,
            dueDate: this.buildDueDateParam(),
            ccRecipients: this.form.ccEmails,
          };
          task = await ProjectTasksService.createProjectTask(params);
          Toast.success({ body: this.$t('projectTasks.toast.createSuccess').toString() });
        }
        this.hide();
        if (this.onSaved) {
          this.onSaved();
        }
      } catch {
        Toast.error({ body: this.$t('projectTasks.modal.errors.saveError').toString() });
      } finally {
        this.isSaving = false;
      }
    },
  },
});
